import { PrivacyIssueRecommendedAction } from '@/services/organization/product/recommendedAction/types'

import { ProductRecommendedAction } from '../recommendedAction/types'

import {
  Law,
  LevelOfImportance,
  ProductPrivacyIssue,
  ProductPrivacyIssueAnswer,
  ScoreStatus,
} from './types'

export const ApplicableLawSentinelValue = 'ALWAYSINCLUDE'

// The answer-option `type` value that marks an N/A ("optional") answer. Kept as
// a bare string literal rather than importing the db enum: the mock-api chunk is
// dynamically imported precisely to keep the TypeORM entity graph out of the main
// bundle, so this core util must not pull it back in. Mirrors the backend
// SCORING_EXCLUDED_OPTION_TYPES invariant (apps/backend/src/utils/scoring).
const NA_ANSWER_OPTION_TYPE = 'optional'

// True when the question was answered with an N/A ("optional") option. Such a
// question must never be classified as bad or unclear practice; it is treated as
// "no action needed" (good practice) so no recommended actions surface for it.
export const isAnsweredNa = (answers: ProductPrivacyIssueAnswer[] | undefined): boolean => Boolean(
  answers?.some(answer => String(answer.answer?.type) === NA_ANSWER_OPTION_TYPE),
)

const parseUserEnteredValue = (value: unknown): unknown => {
  if (typeof value !== 'string') {
    return value
  }
  try {
    const parsed = JSON.parse(value)

    // a bare number like "2024" is valid JSON but still the text the user typed
    return Array.isArray(parsed) ? parsed : value
  }
  catch {
    return value
  }
}

// The text a user typed into a free-text question. The backend stores it as a
// JSON-stringified array copied onto every option row, and only some endpoints
// parse it, so accept either form and de-duplicate across rows.
export const getFreeTextAnswerText = (answers: ProductPrivacyIssueAnswer[] | undefined): string => {
  const values = (answers || [])
    .flatMap(answer => parseUserEnteredValue(answer.userEnteredValue))
    .filter((value): value is string => typeof value === 'string')
    .map(value => value.trim())
    .filter(Boolean)

  return [ ...new Set(values) ].join(', ')
}

export const isAnsweredWithUnclearPractice = (score: number, maxScore: number, answers: ProductPrivacyIssueAnswer[] | undefined) => score === 0 && maxScore > 0 && !answers?.length

export const isAnsweredWithBadPractice = (score: number, maxScore: number, answers: ProductPrivacyIssueAnswer[] | undefined) => (
  score >= 0 && score < maxScore && maxScore > 0 && !!answers?.length && !isAnsweredNa(answers)
)

export const isAnsweredWithGoodPractice = (score: number, maxScore: number, answers?: ProductPrivacyIssueAnswer[] | undefined) => score === maxScore || isAnsweredNa(answers)

export const getProductPrivacyIssueStatus = (productPrivacyIssue: ProductPrivacyIssue): ScoreStatus => {
  const { maxScore } = productPrivacyIssue.privacyIssue
  const answers = productPrivacyIssue?.answers
  const score = getScore(productPrivacyIssue)

  if (isAnsweredWithGoodPractice(score, maxScore, answers)) {
    return 'good'
  }

  if (isAnsweredWithBadPractice(score, maxScore, answers)) {
    return 'normal'
  }

  return 'bad'
}

export const transformProductPrivacyIssue = (productPrivacyIssue: ProductPrivacyIssue | undefined): {
  id: string,
  code: string,
  isClear: boolean,
  importanceLevel: LevelOfImportance,
  question: string,
  score: number,
  maxScore: number,
  answerStatement: string,
  status: ScoreStatus,
  tags: string[],
  type: string,
  title: string,
  answers: ProductPrivacyIssueAnswer[],
} => {
  if (!productPrivacyIssue) {
    return {
      id: '',
      code: '',
      isClear: false,
      importanceLevel: 'Low',
      question: '',
      score: 0,
      maxScore: 0,
      answerStatement: '',
      status: 'bad',
      tags: [],
      type: '',
      title: '',
      answers: [],
    }
  }

  let score = 0
  let answerStatement = ''

  const tags:string[] = []

  const {
    id,
    privacyIssue: {
      code,
      maxScore,
      importanceLevel,
      question,
      applicableLaws,
      privacyTopic: { title },
      type,
      title: privacyIssueTitle,
    },
    answers,
  } = productPrivacyIssue

  const isClear = !!answers && answers.length > 0
  tags.push(title)
  if (applicableLaws) {
    applicableLaws.forEach((law: Law) => {
      law.abbrev !== ApplicableLawSentinelValue && tags.push(law.abbrev)
    })
  }
  tags.sort((a, b) => a.localeCompare(b))

  if (isClear) {
    answers.forEach((answer: ProductPrivacyIssueAnswer) => {
      score += answer.answer?.points || 0
      // statement is nullable (e.g. free-text options)
      answerStatement = answer.answer?.statement || answerStatement
    })
  }

  if (isClear && productPrivacyIssue.productRecommendedActions) {
    const recommendedActionScore = productPrivacyIssue.productRecommendedActions.reduce((
      acc: number,
      productRecommendedAction: ProductRecommendedAction,
    ) => {
      const {
        answers,
        recommendedAction: { privacyIssueRecommendedActions },
      } = productRecommendedAction
      const privacyIssueRecommendedAction = privacyIssueRecommendedActions
        && privacyIssueRecommendedActions.find((privacyIssueRecommendedAction: PrivacyIssueRecommendedAction) => privacyIssueRecommendedAction.privacyIssue.id === productPrivacyIssue.privacyIssue.id)
      if (privacyIssueRecommendedAction && answers?.length) {
        acc += answers.reduce((acc2: number, answer) => {
          acc2 += answer?.answer?.isBetterPractice ? privacyIssueRecommendedAction?.points || 0 : 0

          return acc2
        }, 0)
      }

      return acc
    }, 0)
    score += recommendedActionScore
  }

  return {
    id,
    code,
    isClear,
    importanceLevel: importanceLevel as LevelOfImportance,
    question,
    score,
    maxScore,
    answerStatement,
    status: getProductPrivacyIssueStatus(productPrivacyIssue),
    tags,
    title: privacyIssueTitle,
    type,
    answers: answers || [],
  }
}

export const getScore = (productPrivacyIssue: ProductPrivacyIssue | undefined) => productPrivacyIssue?.answers?.reduce((
  acc: number,
  issue: ProductPrivacyIssueAnswer,
) => acc + (issue.answer?.points || 0), 0) || 0
